import { useState, useEffect } from 'react';
import {
  Activity,
  ArrowRightLeft,
  UserCheck,
  FileText,
  Tag,
  Gift,
  Server,
  AlertCircle,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { PartnershipActivityLogEntry } from '../../types/partnership';
import { partnershipsApiService } from '../../services/partnershipsApi';
import { usernameCacheService } from '../../services/usernameCache';
import { CreatorProfile } from '../CreatorProfile';
import { cn } from '../../utils/cn';
import { SectionHeader } from '../ui/SectionHeader';
import { EmptyState } from '../ui/EmptyState';
import { SkeletonLoader } from '../SkeletonLoader';

const ACTION_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  status_change: ArrowRightLeft,
  assigned: UserCheck,
  note_added: FileText,
  code_created: Tag,
  incentive_added: Gift,
  server_linked: Server,
};

function getActionIcon(action: string) {
  return ACTION_ICONS[action] ?? Activity;
}

export interface PartnershipActivityLogProps {
  partnershipId: string;
}

export function PartnershipActivityLog({ partnershipId }: PartnershipActivityLogProps) {
  const [entries, setEntries] = useState<PartnershipActivityLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    partnershipsApiService
      .getActivityLog(partnershipId)
      .then((data) => {
        if (!cancelled) {
          setEntries(data);
          const actorIds = data.map((e) => e.actor_id).filter(Boolean);
          if (actorIds.length > 0) {
            usernameCacheService.prefetchUsernames(actorIds);
          }
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load activity log');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [partnershipId]);

  if (loading) {
    return (
      <div className="space-y-4" aria-busy="true">
        <SkeletonLoader className="h-4 w-28" />
        <div className="space-y-5">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="flex gap-4">
              <SkeletonLoader className="w-6 h-6 rounded-full shrink-0" />
              <div className="flex-1 space-y-2">
                <SkeletonLoader className="h-3.5 w-3/4" />
                <SkeletonLoader className="h-3 w-40" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <EmptyState
        bare
        compact
        tone="danger"
        icon={AlertCircle}
        title="Failed to load activity"
        description={error}
      />
    );
  }

  return (
    <div className="space-y-4">
      <SectionHeader title="Activity Log" icon={Activity} count={entries.length} />

      {entries.length === 0 ? (
        <EmptyState
          bare
          compact
          icon={Activity}
          title="No activity yet"
          description="Status changes, assignments and additions will be logged here."
        />
      ) : (
        <div className="relative">
          {/* Vertical line */}
          <div className="absolute left-[11px] top-2 bottom-2 w-px bg-glass-200/20" />

          <div className="space-y-0">
            {entries.map((entry) => {
              const Icon = getActionIcon(entry.action);
              return (
                <div
                  key={entry.id}
                  className={cn('relative flex gap-4', 'pb-5 last:pb-0')}
                >
                  {/* Dot */}
                  <div className="relative z-10 flex-shrink-0 w-6 h-6 rounded-full bg-accent-500/10 border border-accent-500/30 flex items-center justify-center">
                    <Icon className="w-3 h-3 text-accent-300" />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0 pt-0.5">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm text-glass-200">{entry.description}</span>
                      {entry.old_value != null && entry.new_value != null && (
                        <span className="text-xs text-glass-500 font-mono">
                          {entry.old_value} → {entry.new_value}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <CreatorProfile creatorId={entry.actor_id} inline />
                      <span className="text-xs text-glass-500 tabular-nums">
                        {formatDistanceToNow(new Date(entry.created_at > 1e12 ? entry.created_at : entry.created_at * 1000), {
                          addSuffix: true,
                        })}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
